import React, { useRef, useState } from "react";
import { classNames } from "../../helpers/classNames";
import CompressImage from "../elements/CompressedImage";
import { toast } from "react-toastify";

const FileUploadandDownload = ({
  required,
  value,
  onRemove = () => {},
  onChange = () => {},
  labelClasses,
  label,
  fileType,
  name,
  errorType,
  errorText,
  size = "full",
  placeholder = "Choose File",
  containerClasses,
  placeholderClass,
  accept,
  iconStyleClass,
  uploadSize,
  uploadFile,
  uploadButtonText,
  downloadButtonClick = () => {},
}) => {
  const inputRef = useRef(null);
  const [image, setImage] = useState(null);

  const handleImageChange = (event) => {
    const acceptList = accept?.split(",").map((elm) => elm.trim());
    const file = event.target.files[0];

    if (!file) return;

    if (!acceptList) {
      setImage(file);
      onChange(file);
      event.target.value = "";
      return;
    }

    const fileExt = file.name.split(".").pop().toLowerCase();
    const fileType = file.type;

    const isValid = acceptList?.some((type) => {
      if (type.startsWith(".")) {
        return fileExt === type.replace(".", "").toLowerCase();
      }
      return fileExt === type || fileType === type;
    });

    if (!isValid) {
      toast.info(`Upload only ${acceptList?.join(", ")}`);
      event.target.value = "";
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (uploadSize && fileSizeMB > uploadSize) {
      toast.warning(`File size must be less than ${uploadSize} MB`);
      event.target.value = "";
      return;
    }

    setImage(file);
    onChange(file);
    event.target.value = "";
  };

  const handleRemoveImage = () => {
    setImage(null);
    onChange("");
    onRemove();
  };

  return (
    <div className="relative w-full bg-chestnut-rose/5 border-l-4 border-l-chestnut-rose/50 rounded-r-xl py-3 px-4">
      {label && (
        <div
          className={classNames(
            "text-[14px] font-medium text-slate-600 mb-1 font-CanelaDeck",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span
              className={classNames(
                "text-[8px] text-red-500 ml-1 absolute top-[10px]",
                iconStyleClass
              )}
            >
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}

      {/* Flex row for download and upload */}
      <div className="flex items-center gap-4">
        {uploadFile?.url && (
          <button
            onClick={() => downloadButtonClick(uploadFile)}
            type="button"
            className="!bg-white !p-3 !w-auto !inline-flex !pr-3 !text-chestnut-rose border !border-chestnut-rose !rounded-full text-sm font-CanelaDeck pl-2"
          >
            {uploadButtonText || "Download File"}
          </button>
        )}

        <div
          className={classNames(
            size === "full"
              ? "cursor-pointer flex flex-col items-center justify-center overflow-hidden bg-chestnut-rose/5 border-2 border-chestnut-rose border-dashed rounded-lg h-40"
              : "cursor-pointer relative w-full h-10 flex items-center focus:ring-0 transition-all duration-200 gap-2 overflow-hidden p-1 rounded-md bg-white border border-shades-05/50 ",
            containerClasses
          )}
        >
          {image || value ? (
            <>
              <div
                className={classNames(
                  size === "full"
                    ? "relative h-full w-full"
                    : "h-full min-w-10 w-10 rounded shadow border border-slate-200 overflow-hidden"
                )}
              >
                <CompressImage
                  src={
                    (image || value) instanceof File
                      ? URL.createObjectURL(image || value)
                      : image || value
                  }
                  quality={0.4}
                  alt="Preview"
                  className="w-full h-full object-cover object-center"
                />
                {size === "full" && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute hover:scale-[1.2] transition-all duration-200 top-1/2 left-1/2 bottom-1/2 right-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-red-500 text-white rounded-full px-2 py-1 text-xs"
                  >
                    <i className="fa-fw fa-regular fa-trash-can"></i>
                  </button>
                )}
              </div>
              {size === "small" && (
                <>
                  <div className="text-xs text-secondayColor truncate w-[calc(100%-90px)]">
                    {(image || value) instanceof File
                      ? (image || value)?.name
                      : name || image || value}
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-red-500 ml-auto w-10"
                  >
                    <i className="fa-fw fa-regular fa-trash-can"></i>
                  </button>
                </>
              )}
            </>
          ) : (
            <label
              className={classNames(
                size === "full"
                  ? "relative cursor-pointer w-full h-full flex flex-col items-center justify-center"
                  : ""
              )}
            >
              {size === "full" && (
                <>
                  <img
                    src={require("../../assets/images/imageUpload.png")}
                    alt="icon"
                    className="w-10 h-10 opacity-80"
                  />
                  <div className="text-sm text-shades-07 mt-2">
                    <span className="text-chestnut-rose font-semibold underline">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </div>
                </>
              )}
              {size === "small" && (
                <span
                  className={classNames(
                    "text-sm font-CanelaDeck pl-2",
                    placeholderClass || "text-shades-07"
                  )}
                >
                  {placeholder}
                </span>
              )}
              <input
                type="file"
                ref={inputRef}
                className="opacity-0 absolute h-full w-[200%] top-0 right-0 cursor-pointer z-10"
                accept={accept}
                onChange={handleImageChange}
              />
            </label>
          )}
        </div>
      </div>

      {/* Error or status messages */}
      {errorType === "danger" && (
        <div className="text-xs font-medium text-red-600 mt-1">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "warning" && (
        <div className="text-xs font-medium text-amber-500 mt-1">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "success" && (
        <div className="text-xs font-medium text-green-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "info" && (
        <div className="text-xs font-medium text-sky-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default FileUploadandDownload;
